import React from 'react';
import { FileText, CheckCircle, XCircle, Scale, AlertTriangle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';

const TermsOfService = () => {
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center py-8">
        <div className="w-16 h-16 bg-reddit-green rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-4xl font-bold mb-4">Terms of Service</h1>
        <p className="text-lg text-reddit-600 dark:text-dark-textMuted max-w-3xl mx-auto">
          Please read these terms carefully before using Trendy
        </p>
        <p className="text-sm text-reddit-600 dark:text-dark-textMuted mt-2">
          Last Updated: January 27, 2026
        </p>
      </div>

      {/* Agreement */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Scale className="w-6 h-6 text-reddit-orange" />
            Agreement to Terms
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            By accessing or using Trendy ("the Service"), you agree to be bound by these Terms of Service ("Terms"). 
            If you disagree with any part of these terms, you may not access the Service.
          </p>
          
          <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
            <h4 className="font-semibold mb-2">Key Points</h4>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>You must be at least 13 years old to use Trendy</li>
              <li>You are responsible for maintaining account security</li>
              <li>You agree to use the Service lawfully and ethically</li>
              <li>We may modify these terms at any time</li>
              <li>You can stop using the Service at any time</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Use of Service */}
      <Card>
        <CardHeader>
          <CardTitle>Use of Service</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-reddit-green" />
              Permitted Use
            </h4>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-2">
              You may use Trendy for the following purposes:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm pl-4">
              <li>Personal use to discover trending topics</li>
              <li>Research and analysis of trending data</li>
              <li>Educational purposes</li>
              <li>Integration with your applications (subject to API terms)</li>
              <li>Sharing insights and data visualizations</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-reddit-red" />
              Prohibited Use
            </h4>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-2">
              You must NOT use Trendy for:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm pl-4">
              <li>Any illegal or unauthorized purpose</li>
              <li>Violating laws in your jurisdiction</li>
              <li>Transmitting malicious code or viruses</li>
              <li>Attempting to gain unauthorized access</li>
              <li>Harassing, abusing, or harming others</li>
              <li>Impersonating any person or entity</li>
              <li>Interfering with the Service's operation</li>
              <li>Scraping or harvesting data without permission</li>
              <li>Reverse engineering the Service</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Accounts */}
      <Card>
        <CardHeader>
          <CardTitle>Accounts and Registration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>
            Currently, Trendy operates without requiring user accounts. Your preferences and favorites are stored 
            locally in your browser. However, we may introduce user accounts in the future.
          </p>

          <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
            <h4 className="font-semibold mb-2">If Accounts Are Introduced</h4>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>You will be responsible for maintaining account security</li>
              <li>You must provide accurate information</li>
              <li>You are responsible for all activities under your account</li>
              <li>You must notify us of any unauthorized access</li>
              <li>We reserve the right to suspend or terminate accounts</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      <Card>
        <CardHeader>
          <CardTitle>Content and Intellectual Property</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Our Content</h4>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-2">
              The Service and its original content, features, and functionality are owned by Trendy and are protected by:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm pl-4">
              <li>International copyright laws</li>
              <li>Trademark laws</li>
              <li>Patent laws</li>
              <li>Other intellectual property rights</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Third-Party Content</h4>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
              Trendy aggregates data from third-party sources (Twitter, Reddit, Google Trends). We do not claim 
              ownership of this data. All third-party content is subject to the respective platform's terms of service 
              and intellectual property rights.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Your Use of Content</h4>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
              You may view and use the trending data for personal, non-commercial purposes. You may share visualizations 
              and insights with proper attribution to Trendy and the original data sources.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* API Usage */}
      <Card>
        <CardHeader>
          <CardTitle>API Usage Terms</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p>If you use Trendy's API, you agree to the following additional terms:</p>
          
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-reddit-orange text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                1
              </div>
              <div>
                <h4 className="font-semibold">Rate Limits</h4>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                  Respect rate limits specified in the API documentation
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-reddit-orange text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                2
              </div>
              <div>
                <h4 className="font-semibold">API Keys</h4>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                  Keep your API keys secure and do not share them publicly
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-reddit-orange text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                3
              </div>
              <div>
                <h4 className="font-semibold">Attribution</h4>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                  Provide proper attribution when using API data
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-reddit-orange text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                4
              </div>
              <div>
                <h4 className="font-semibold">Caching</h4>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                  Cache responses appropriately to minimize requests
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-reddit-orange text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm">
                5
              </div>
              <div>
                <h4 className="font-semibold">Monitoring</h4>
                <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
                  We monitor API usage to prevent abuse
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Disclaimer */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-reddit-orange" />
            Disclaimer of Warranties
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="font-semibold">
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND.
          </p>
          
          <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
            We do not warrant that:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm pl-4">
            <li>The Service will be uninterrupted, timely, secure, or error-free</li>
            <li>The results obtained from the Service will be accurate or reliable</li>
            <li>The quality of any data or information obtained will meet your expectations</li>
            <li>Any errors in the Service will be corrected</li>
          </ul>

          <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
            <p className="text-sm font-semibold mb-2">Data Accuracy</p>
            <p className="text-sm">
              While we strive for accuracy, trending data is aggregated from third-party sources and may contain 
              inaccuracies or be out of date. We are not responsible for decisions made based on this data.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Limitation of Liability */}
      <Card>
        <CardHeader>
          <CardTitle>Limitation of Liability</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, TRENDY SHALL NOT BE LIABLE FOR:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm pl-4">
            <li>Any indirect, incidental, special, consequential, or punitive damages</li>
            <li>Loss of profits, revenue, data, or business opportunities</li>
            <li>Service interruptions or data loss</li>
            <li>Errors or inaccuracies in data</li>
            <li>Unauthorized access to your data</li>
            <li>Third-party actions or content</li>
          </ul>

          <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
            <p className="text-sm">
              Our total liability shall not exceed the amount paid by you (if any) for accessing the Service 
              in the past twelve months.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Indemnification */}
      <Card>
        <CardHeader>
          <CardTitle>Indemnification</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-3">
            You agree to indemnify, defend, and hold harmless Trendy and its officers, directors, employees, and 
            agents from any claims, liabilities, damages, losses, and expenses arising from:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm pl-4">
            <li>Your use of the Service</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of another party</li>
            <li>Your violation of any applicable laws</li>
          </ul>
        </CardContent>
      </Card>

      {/* Termination */}
      <Card>
        <CardHeader>
          <CardTitle>Termination</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">By You</h4>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
              You may stop using the Service at any time. If accounts are introduced, you may close your account 
              at any time through the settings page.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-2">By Us</h4>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-2">
              We may terminate or suspend your access immediately, without prior notice, for any reason, including:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm pl-4">
              <li>Violation of these Terms</li>
              <li>Fraudulent or illegal activity</li>
              <li>Abuse of the Service</li>
              <li>Extended periods of inactivity</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-2">Effect of Termination</h4>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
              Upon termination, your right to use the Service will immediately cease. All provisions that should 
              survive termination shall survive, including ownership provisions, warranty disclaimers, and 
              limitations of liability.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Changes to Terms */}
      <Card>
        <CardHeader>
          <CardTitle>Changes to Terms</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-3">
            We reserve the right to modify these Terms at any time. We will notify users of any material changes by:
          </p>
          <ul className="list-disc list-inside space-y-1 text-sm pl-4 mb-3">
            <li>Posting the new Terms on this page</li>
            <li>Updating the "Last Updated" date</li>
            <li>Sending an email notification (if applicable)</li>
          </ul>
          <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
            Your continued use of the Service after changes take effect constitutes acceptance of the modified Terms.
          </p>
        </CardContent>
      </Card>

      {/* Governing Law */}
      <Card>
        <CardHeader>
          <CardTitle>Governing Law</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
            These Terms shall be governed by and construed in accordance with applicable laws, without regard to 
            conflict of law provisions. Any disputes arising from these Terms or the Service shall be resolved 
            through arbitration or in courts of competent jurisdiction.
          </p>
        </CardContent>
      </Card>

      {/* Contact */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-reddit-600 dark:text-dark-textMuted mb-4">
            If you have any questions about these Terms, please contact us:
          </p>
          <div className="bg-reddit-50 dark:bg-dark-border p-4 rounded-lg">
            <p className="text-sm"><strong>Email:</strong> trendy@gmail.com</p>
            <p className="text-sm"><strong>GitHub:</strong> github.com/julzlalu2224/Trendy</p>
            <p className="text-sm"><strong>Website:</strong> trendy.com</p>
          </div>
        </CardContent>
      </Card>

      {/* Acceptance */}
      <Card>
        <CardContent className="pt-6">
          <div className="bg-reddit-50 dark:bg-dark-border p-6 rounded-lg text-center">
            <p className="font-semibold mb-2">By using Trendy, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</p>
            <p className="text-sm text-reddit-600 dark:text-dark-textMuted">
              If you do not agree to these Terms, please do not use the Service.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TermsOfService;
